import { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';

const SERVICES_DROPDOWN = [
  { label: 'AI Solutions', href: '/services/ai-solutions', icon: 'ri-brain-line' },
  { label: 'Web Applications', href: '/services/web-applications', icon: 'ri-global-line' },
  { label: 'Windows Applications', href: '/services/windows-applications', icon: 'ri-window-line' },
  { label: 'Mobile Applications', href: '/services/mobile-applications', icon: 'ri-smartphone-line' },
];

const PRODUCTS_DROPDOWN = [
  { label: 'Magic Designing Software', href: '/products#magic-design', icon: 'ri-magic-line' },
  { label: 'Photo Press & Ebook', href: '/products#photo-press', icon: 'ri-image-edit-line' },
  { label: 'Ship Chandling Quotation', href: '/products#ship-chandling', icon: 'ri-ship-line' },
  { label: 'Inventory Management', href: '/products#inventory', icon: 'ri-store-3-line' },
  { label: 'POS Solution', href: '/products#pos', icon: 'ri-shopping-cart-2-line' },
  { label: 'RFID Based Solution', href: '/products#rfid', icon: 'ri-nfc-line' },
  { label: 'RFID Library Management', href: '/products#rfid-library', icon: 'ri-book-2-line' },
];

const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'About Us', href: '/about' },
  { label: 'Services', href: '/services', dropdown: SERVICES_DROPDOWN, dropdownKey: 'services' },
  { label: 'Products', href: '/products', dropdown: PRODUCTS_DROPDOWN, dropdownKey: 'products', noNavigate: true },
  { label: 'Portfolio', href: '/portfolio' },
  { label: 'Industries', href: '/industries' },
  { label: 'Our Clients', href: '/clients' },
  { label: 'Contact Us', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const handleDropdownLinkClick = (href: string) => {
    setActiveDropdown(null);
    setMenuOpen(false);
    if (href.includes('#')) {
      const [path, hash] = href.split('#');
      if (location.pathname === path) {
        setTimeout(() => {
          document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white border-b border-gray-100' : 'bg-white/90 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-24">
        {/* Logo */}
        <Link to="/" className="cursor-pointer flex items-center gap-3 flex-shrink-0">
          <div className="flex items-center justify-center" style={{ width: '104px', height: '104px' }}>
            <img
              src="https://static.readdy.ai/image/470712cea6bd611f3a1669acb3b30c19/7df20f577f8643a2dddf858949ce508a.png"
              alt="IT Services Logo"
              className="object-contain"
              style={{ width: '104px', height: '104px' }}
            />
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-0.5">
          {NAV_LINKS.map((link) =>
            link.dropdown ? (
              <div
                key={link.href}
                className="relative"
                onMouseEnter={() => setActiveDropdown(link.dropdownKey ?? null)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.noNavigate ? (
                  <button
                    className={`flex items-center gap-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                      location.pathname.startsWith('/products')
                        ? 'text-teal-600 bg-teal-50'
                        : 'text-gray-600 hover:text-teal-600 hover:bg-gray-50'
                    }`}
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line text-base transition-transform ${activeDropdown === link.dropdownKey ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <NavLink
                    to={link.href}
                    className={({ isActive }) =>
                      `flex items-center gap-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                        isActive ? 'text-teal-600 bg-teal-50' : 'text-gray-600 hover:text-teal-600 hover:bg-gray-50'
                      }`
                    }
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line text-base transition-transform ${activeDropdown === link.dropdownKey ? 'rotate-180' : ''}`} />
                  </NavLink>
                )}

                {activeDropdown === link.dropdownKey && (
                  <div className="absolute top-full left-0 mt-1 w-60 bg-white border border-gray-100 rounded-xl overflow-hidden">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => handleDropdownLinkClick(item.href)}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-600 hover:bg-teal-50 hover:text-teal-700 transition-colors cursor-pointer"
                      >
                        <div className="w-7 h-7 flex items-center justify-center text-teal-500">
                          <i className={`${item.icon} text-base`} />
                        </div>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={link.href}
                to={link.href}
                end={link.href === '/'}
                className={({ isActive }) =>
                  `px-4 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                    isActive ? 'text-teal-600 bg-teal-50' : 'text-gray-600 hover:text-teal-600 hover:bg-gray-50'
                  }`
                }
              >
                {link.label}
              </NavLink>
            )
          )}
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <Link
            to="/contact"
            className="cursor-pointer whitespace-nowrap bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
          >
            Get a Free Consultation
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer text-gray-700"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-2xl`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) =>
              link.dropdown ? (
                <div key={link.href}>
                  <button
                    onClick={() => {
                      if (link.dropdownKey === 'services') setMobileServicesOpen(!mobileServicesOpen);
                      if (link.dropdownKey === 'products') setMobileProductsOpen(!mobileProductsOpen);
                    }}
                    className="w-full flex items-center justify-between text-gray-700 hover:text-teal-600 font-medium py-2.5 cursor-pointer text-sm"
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line transition-transform ${(link.dropdownKey === 'services' && mobileServicesOpen) || (link.dropdownKey === 'products' && mobileProductsOpen) ? 'rotate-180' : ''}`} />
                  </button>
                  {((link.dropdownKey === 'services' && mobileServicesOpen) || (link.dropdownKey === 'products' && mobileProductsOpen)) && (
                    <div className="pl-4 flex flex-col gap-1 mb-1">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => { setMenuOpen(false); handleDropdownLinkClick(item.href); }}
                          className="flex items-center gap-2 text-gray-500 hover:text-teal-600 text-sm py-2 cursor-pointer"
                        >
                          <i className={`${item.icon} text-teal-500`} /> {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={link.href}
                  to={link.href}
                  end={link.href === '/'}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `text-sm font-medium py-2.5 cursor-pointer ${isActive ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'}`
                  }
                >
                  {link.label}
                </NavLink>
              )
            )}
            <Link
              to="/contact"
              onClick={() => setMenuOpen(false)}
              className="cursor-pointer whitespace-nowrap bg-teal-500 text-white text-sm font-semibold px-5 py-3 rounded-lg text-center mt-3"
            >
              Get a Free Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
